import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageSeo from '@/components/feature/PageSeo';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PostCard from '@/components/feature/PostCard';
import NewsletterSubscribe from '@/components/feature/NewsletterSubscribe';
import { channels } from '@/mocks/channels';
import { posts } from '@/mocks/posts';
import { getChannelBySlug } from '@/lib/content';

const sortOptions = [
  { key: 'hot', label: '按热度' },
  { key: 'latest', label: '按时间' },
  { key: 'loved', label: '按点赞' },
];

export default function ChannelPage() {
  const { slug = '' } = useParams();
  const channel = getChannelBySlug(slug);
  const [sortKey, setSortKey] = useState('hot');

  const channelPosts = useMemo(() => {
    const list = posts.filter((post) => post.channel === slug);
    if (sortKey === 'latest') return [...list].sort((a, b) => b.date.localeCompare(a.date));
    if (sortKey === 'loved') return [...list].sort((a, b) => b.likes - a.likes);
    return [...list].sort((a, b) => b.heat - a.heat);
  }, [slug, sortKey]);

  if (!channel) {
    return (
      <>
        <SiteHeader />
        <main className="mx-auto flex max-w-[1400px] flex-col items-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-ghost-line w-8 h-8 flex items-center justify-center text-4xl" />
          </span>
          <h1 className="mt-6 font-heading text-2xl text-foreground-950">这个专栏还没开张</h1>
          <p className="mt-3 text-sm text-foreground-600">它可能改名了，或者还没上线。</p>
          <Link
            to="/"
            className="mt-8 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            回到首页
          </Link>
        </main>
        <SiteFooter />
      </>
    );
  }

  const channelJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${channel.name} - 糖心吃瓜网`,
    description: channel.desc,
    url: `https://renrenmingxiao.cn/channel/${channel.slug}`,
    inLanguage: 'zh-CN',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: channelPosts.map((post, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: post.title,
        url: `https://renrenmingxiao.cn/post/${post.id}`,
      })),
    },
  };

  return (
    <>
      <PageSeo
        title={`${channel.name} - 糖心吃瓜网${channel.name}精选内容合集`}
        description={`${channel.desc}糖心吃瓜网${channel.name}专栏现有 ${channel.count} 条内容，持续更新，适合下饭、摸鱼与睡前随手刷。`}
        keywords={`糖心吃瓜,${channel.name},糖心吃瓜网,摸鱼笑料`}
        canonical={`https://renrenmingxiao.cn/channel/${channel.slug}`}
        jsonLd={channelJsonLd}
      />
      <ScrollToTop />
      <SiteHeader />
      <Breadcrumb items={[{ label: channel.name }]} />

      <main>
        <section className="border-b border-background-200/70 bg-background-100/60">
          <div className="mx-auto max-w-[1400px] px-4 py-12 md:px-6 md:py-14">
            <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
              <div className="max-w-2xl">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-100 text-primary-700">
                  <i className={`${channel.icon} w-7 h-7 flex items-center justify-center text-3xl`} />
                </span>
                <h1 className="mt-5 font-heading text-3xl text-foreground-950 md:text-4xl">
                  {channel.name}
                </h1>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                  {channel.desc}
                </p>
              </div>
              <div className="flex flex-wrap gap-3 text-sm">
                <span className="rounded-lg border border-background-200 bg-background-50 px-4 py-2 text-foreground-700">
                  共 <strong className="text-primary-700">{channel.count}</strong> 条内容
                </span>
                <span className="rounded-lg border border-background-200 bg-background-50 px-4 py-2 text-foreground-700">
                  每日更新
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[1400px] px-4 py-12 md:px-6 md:py-14">
          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-9">
              <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
                <h2 className="font-heading text-xl text-foreground-950">
                  本专栏内容
                  <span className="ml-2 text-sm font-normal text-foreground-500">
                    {channelPosts.length} 条
                  </span>
                </h2>
                <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-50 p-1">
                  {sortOptions.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => setSortKey(option.key)}
                      className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                        sortKey === option.key
                          ? 'bg-primary-500 font-medium text-background-50'
                          : 'text-foreground-600 hover:text-primary-700'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {channelPosts.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
                  <p className="text-sm text-foreground-600">
                    这个专栏的内容正在路上，先去其他专栏逛逛吧。
                  </p>
                </div>
              ) : (
                <div className="grid gap-5 sm:grid-cols-2">
                  {channelPosts.map((post) => (
                    <PostCard key={post.id} post={post} />
                  ))}
                </div>
              )}
            </div>

            <aside className="lg:col-span-3">
              <div className="sticky top-28 space-y-6">
                <div className="rounded-2xl border border-background-200/70 bg-background-50 p-5">
                  <h3 className="font-heading text-base text-foreground-950">换个专栏看看</h3>
                  <ul className="mt-4 space-y-2">
                    {channels
                      .filter((item) => item.slug !== channel.slug)
                      .map((item) => (
                        <li key={item.slug}>
                          <Link
                            to={`/channel/${item.slug}`}
                            className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                          >
                            <i className={`${item.icon} w-4 h-4 flex items-center justify-center text-base`} />
                            {item.name}
                            <span className="ml-auto text-xs text-foreground-400">
                              {item.count}
                            </span>
                          </Link>
                        </li>
                      ))}
                  </ul>
                </div>

                <div className="rounded-2xl border border-secondary-200/70 bg-secondary-50/70 p-5">
                  <h3 className="font-heading text-base text-foreground-950">有更好笑的瓜？</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                    欢迎把你遇到的名场面投稿给我们，被采用后会署名展示。
                  </p>
                  <Link
                    to="/about#contact"
                    className="mt-4 inline-flex whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-600"
                  >
                    我要投稿
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </section>

        <NewsletterSubscribe />
      </main>

      <SiteFooter />
      <BackToTop />
    </>
  );
}